import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { HACKATHONS, MY_HACKATHON_TEAMS, CREATE_HACKATHON, CREATE_HACKATHON_TEAM, JOIN_HACKATHON_TEAM, LEAVE_HACKATHON_TEAM } from '../graphql';
import { Zap, Plus, X, Users, Calendar, ExternalLink, ChevronDown, ChevronUp, Globe, Laptop, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_COLOR = { UPCOMING:'bg-amber-100 text-amber-700', OPEN:'bg-green-100 text-green-700', CLOSED:'bg-gray-100 text-gray-500', COMPLETED:'bg-blue-100 text-blue-700' };
const MODE_ICON = { ONLINE: <Globe size={12}/>, OFFLINE: <MapPin size={12}/>, HYBRID: <Laptop size={12}/> };

function TeamCard({ team, currentUserId, onJoin, onLeave, hackathonId }) {
  const [roleInput, setRoleInput] = useState('');
  const isMember = team.members?.some(m => m.user?.id === currentUserId);
  const isLeader = team.leader?.id === currentUserId;

  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <div className="flex items-center justify-between mb-1">
        <div>
          <span className="font-semibold text-sm text-gray-800">{team.teamName}</span>
          {team.isOpen && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Open</span>}
        </div>
        <span className="text-xs text-gray-400"><Users size={10} className="inline mr-1"/>{team.memberCount}</span>
      </div>
      {team.projectIdea && <p className="text-xs text-gray-500 mb-1">{team.projectIdea}</p>}
      {team.skillsNeeded?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {team.skillsNeeded.map(s => <span key={s} className="text-xs bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded">{s}</span>)}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex -space-x-1">
          {team.members?.slice(0,5).map(m => (
            <div key={m.user?.id} className="w-5 h-5 rounded-full bg-brand-200 flex items-center justify-center text-xs text-brand-700 font-bold border border-white" title={`${m.user?.name} (${m.role})`}>
              {m.user?.name?.[0]}
            </div>
          ))}
        </div>
        {!isLeader && team.isOpen && (
          isMember
            ? <button onClick={() => onLeave(team.id)} className="text-xs text-red-500 hover:underline">Leave</button>
            : (
              <div className="flex gap-1">
                <input className="border border-gray-200 rounded px-1.5 py-0.5 text-xs w-20" value={roleInput} onChange={e=>setRoleInput(e.target.value)} placeholder="Your role" />
                <button onClick={() => { onJoin(team.id, roleInput||'Member'); setRoleInput(''); }} className="text-xs btn-primary px-2 py-0.5">Join</button>
              </div>
            )
        )}
        {isLeader && <span className="text-xs text-brand-600 font-semibold">Leader</span>}
      </div>
    </div>
  );
}

function HackathonCard({ hackathon, currentUserId, onCreateTeam, onJoin, onLeave }) {
  const [expanded, setExpanded] = useState(false);
  const [teamForm, setTeamForm] = useState({ teamName: '', projectIdea: '', skillsNeeded: '', role: '' });
  const [showTeamForm, setShowTeamForm] = useState(false);

  return (
    <div className="card !p-0 overflow-hidden">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-bold text-gray-900">{hackathon.title}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[hackathon.status]}`}>{hackathon.status}</span>
              <span className="text-xs flex items-center gap-1 text-gray-500">{MODE_ICON[hackathon.mode]} {hackathon.mode}</span>
            </div>
            <p className="text-sm text-gray-500 mb-2 line-clamp-2">{hackathon.description}</p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-400">
              {hackathon.startDate && <span className="flex items-center gap-1"><Calendar size={10}/>{new Date(hackathon.startDate).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>}
              {hackathon.endDate && <span>→ {new Date(hackathon.endDate).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>}
              {hackathon.registrationDeadline && <span>Reg by {new Date(hackathon.registrationDeadline).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>}
              <span><Users size={10} className="inline"/> Team: {hackathon.minTeamSize}–{hackathon.maxTeamSize}</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            {hackathon.prizes && <p className="text-xs font-semibold text-amber-600 mb-1">{hackathon.prizes}</p>}
            <div className="flex gap-2 mt-1">
              {hackathon.applyLink && <a href={hackathon.applyLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 btn-secondary text-xs py-1 px-2"><ExternalLink size={11}/> Apply</a>}
              <button onClick={() => setExpanded(e=>!e)} className="btn-secondary text-xs py-1 px-2 flex items-center gap-1">
                {expanded ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}Teams ({hackathon.teams?.length || 0})
              </button>
            </div>
          </div>
        </div>

        {/* Themes */}
        {hackathon.themes?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {hackathon.themes.map(t => <span key={t} className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full">#{t}</span>)}
          </div>
        )}
      </div>

      {/* Teams section */}
      {expanded && (
        <div className="border-t border-gray-100 p-5 bg-gray-50/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Teams Looking for Members</h4>
            {hackathon.status === 'OPEN' && (
              <button onClick={() => setShowTeamForm(s=>!s)} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Plus size={11}/> Create Team</button>
            )}
          </div>

          {showTeamForm && (
            <div className="bg-white rounded-xl p-4 mb-3 border border-brand-100 space-y-2">
              <input className="input w-full text-sm" placeholder="Team name" value={teamForm.teamName} onChange={e=>setTeamForm(f=>({...f,teamName:e.target.value}))} />
              <input className="input w-full text-sm" placeholder="Project idea (optional)" value={teamForm.projectIdea} onChange={e=>setTeamForm(f=>({...f,projectIdea:e.target.value}))} />
              <input className="input w-full text-sm" placeholder="Skills needed (comma-sep: React, ML, Design)" value={teamForm.skillsNeeded} onChange={e=>setTeamForm(f=>({...f,skillsNeeded:e.target.value}))} />
              <input className="input w-full text-sm" placeholder="Your role (e.g. Backend Dev)" value={teamForm.role} onChange={e=>setTeamForm(f=>({...f,role:e.target.value}))} />
              <div className="flex gap-2">
                <button onClick={() => {
                  onCreateTeam(hackathon.id, teamForm.teamName, teamForm.projectIdea, teamForm.skillsNeeded.split(',').map(s=>s.trim()).filter(Boolean), teamForm.role);
                  setShowTeamForm(false);
                  setTeamForm({ teamName:'',projectIdea:'',skillsNeeded:'',role:'' });
                }} disabled={!teamForm.teamName} className="btn-primary text-sm flex-1">Create Team</button>
                <button onClick={() => setShowTeamForm(false)} className="btn-secondary text-sm">Cancel</button>
              </div>
            </div>
          )}

          {hackathon.teams?.length === 0
            ? <p className="text-sm text-gray-400 text-center py-4">No teams yet — be the first to create one.</p>
            : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {hackathon.teams.map(t => (
                  <TeamCard key={t.id} team={t} currentUserId={currentUserId} hackathonId={hackathon.id}
                    onJoin={(teamId, role) => onJoin(teamId, role)}
                    onLeave={(teamId) => onLeave(teamId)} />
                ))}
              </div>
            )
          }
        </div>
      )}
    </div>
  );
}

export default function HackathonPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('browse');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', startDate: '', endDate: '', registrationDeadline: '', maxTeamSize: 4, minTeamSize: 2, themes: '', prizes: '', mode: 'ONLINE', applyLink: '', college: '' });

  const { data, refetch } = useQuery(HACKATHONS, { variables: { status: status || undefined } });
  const { data: myData, refetch: refetchMy } = useQuery(MY_HACKATHON_TEAMS);
  const [createHack, { loading: creating }] = useMutation(CREATE_HACKATHON, { onCompleted: () => { refetch(); setShowCreate(false); }});
  const [createTeam] = useMutation(CREATE_HACKATHON_TEAM, { onCompleted: () => refetch() });
  const [joinTeam] = useMutation(JOIN_HACKATHON_TEAM, { onCompleted: () => refetch() });
  const [leaveTeam] = useMutation(LEAVE_HACKATHON_TEAM, { onCompleted: () => { refetch(); refetchMy(); }});

  const hackathons = data?.hackathons || [];
  const myTeams = myData?.myHackathonTeams || [];

  const handleCreate = async (e) => {
    e.preventDefault();
    await createHack({ variables: { ...form, themes: form.themes.split(',').map(s=>s.trim()).filter(Boolean), maxTeamSize: parseInt(form.maxTeamSize)||4, minTeamSize: parseInt(form.minTeamSize)||2 } });
    setForm({ title:'',description:'',startDate:'',endDate:'',registrationDeadline:'',maxTeamSize:4,minTeamSize:2,themes:'',prizes:'',mode:'ONLINE',applyLink:'',college:'' });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-100 rounded-xl"><Zap size={20} className="text-yellow-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hackathon Hub</h1>
            <p className="text-gray-500 text-sm">Browse hackathons · Build teams · Win prizes</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(true)} className="btn-secondary text-sm flex items-center gap-1.5"><Plus size={14}/> Post Hackathon</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {[['browse','Browse All'],['myteams','My Teams']].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab===t?'bg-white shadow text-gray-900':'text-gray-500'}`}>{l}{t==='myteams' && myTeams.length > 0 ? ` (${myTeams.length})` : ''}</button>
          ))}
        </div>
        {tab === 'browse' && (
          <select className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none" value={status} onChange={e => { setStatus(e.target.value); refetch({ status: e.target.value || undefined }); }}>
            <option value="">All Status</option>
            {['UPCOMING','OPEN','CLOSED','COMPLETED'].map(s => <option key={s}>{s}</option>)}
          </select>
        )}
      </div>

      {/* Stats */}
      {tab === 'browse' && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[['Total',hackathons.length],['Open',hackathons.filter(h=>h.status==='OPEN').length],['Online',hackathons.filter(h=>h.mode==='ONLINE').length],['Teams',hackathons.reduce((s,h)=>s+h.teams?.length,0)]].map(([l,v]) => (
            <div key={l} className="bg-yellow-50 text-yellow-700 rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{v}</p><p className="text-xs font-medium mt-0.5">{l}</p>
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      {tab === 'myteams' ? (
        myTeams.length === 0
          ? <div className="text-center py-16"><Users size={40} className="mx-auto mb-3 text-gray-300" /><p className="text-gray-500">You haven't joined any team yet</p><button onClick={() => setTab('browse')} className="btn-primary mt-3 text-sm">Browse Hackathons</button></div>
          : <div className="space-y-3">
              {myTeams.map(t => (
                <div key={t.id} className="card !p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900">{t.teamName}</h3>
                      {t.projectIdea && <p className="text-sm text-gray-500 mt-0.5">{t.projectIdea}</p>}
                      <div className="flex gap-2 mt-1">
                        {t.members?.map(m => <span key={m.user?.id} className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full">{m.user?.name} ({m.role})</span>)}
                      </div>
                    </div>
                    <button onClick={() => leaveTeam({ variables: { teamId: t.id } })} className="text-xs text-red-400 hover:underline">Leave Team</button>
                  </div>
                </div>
              ))}
            </div>
      ) : (
        hackathons.length === 0
          ? <div className="text-center py-16"><Zap size={40} className="mx-auto mb-3 text-gray-300" /><p className="text-gray-500">No hackathons posted yet</p><button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">Post a Hackathon</button></div>
          : <div className="space-y-4">
              {hackathons.map(h => (
                <HackathonCard key={h.id} hackathon={h} currentUserId={user?.id}
                  onCreateTeam={(hid, name, idea, skills, role) => createTeam({ variables: { hackathonId: hid, teamName: name, projectIdea: idea, skillsNeeded: skills, role } })}
                  onJoin={(teamId, role) => joinTeam({ variables: { teamId, role } })}
                  onLeave={(teamId) => leaveTeam({ variables: { teamId } })} />
              ))}
            </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Post a Hackathon</h2>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <input required className="input w-full" placeholder="Hackathon title" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} />
              <textarea required className="input w-full resize-none h-16 text-sm" placeholder="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-400 mb-1 block">Start</label><input type="date" className="input w-full" value={form.startDate} onChange={e=>setForm(f=>({...f,startDate:e.target.value}))} /></div>
                <div><label className="text-xs text-gray-400 mb-1 block">End</label><input type="date" className="input w-full" value={form.endDate} onChange={e=>setForm(f=>({...f,endDate:e.target.value}))} /></div>
                <div><label className="text-xs text-gray-400 mb-1 block">Reg. Deadline</label><input type="date" className="input w-full" value={form.registrationDeadline} onChange={e=>setForm(f=>({...f,registrationDeadline:e.target.value}))} /></div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Mode</label>
                  <select className="input w-full" value={form.mode} onChange={e=>setForm(f=>({...f,mode:e.target.value}))}>
                    {['ONLINE','OFFLINE','HYBRID'].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div><label className="text-xs text-gray-400 mb-1 block">Min Team Size</label><input type="number" min="1" className="input w-full" value={form.minTeamSize} onChange={e=>setForm(f=>({...f,minTeamSize:e.target.value}))} /></div>
                <div><label className="text-xs text-gray-400 mb-1 block">Max Team Size</label><input type="number" min="1" className="input w-full" value={form.maxTeamSize} onChange={e=>setForm(f=>({...f,maxTeamSize:e.target.value}))} /></div>
              </div>
              <input className="input w-full" placeholder="Themes (comma-sep: AI, Web3, Health)" value={form.themes} onChange={e=>setForm(f=>({...f,themes:e.target.value}))} />
              <input className="input w-full" placeholder="Prizes (e.g. ₹1L for 1st, MacBook for 2nd)" value={form.prizes} onChange={e=>setForm(f=>({...f,prizes:e.target.value}))} />
              <input className="input w-full" placeholder="Registration / Apply link" value={form.applyLink} onChange={e=>setForm(f=>({...f,applyLink:e.target.value}))} />
              <input className="input w-full" placeholder="College (blank = open to all)" value={form.college} onChange={e=>setForm(f=>({...f,college:e.target.value}))} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Posting…' : 'Post Hackathon'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
